import { zodResolver } from '@hookform/resolvers/zod'
import type { ChangeEventHandler } from 'react'
import { useForm } from 'react-hook-form'

import Button from '@src/component/ButtonV2/Button'
import TextArea from '@ui/TextArea'
import Typography from '@ui/Typography'

import * as styles from './CallQualityWizardIssueForm.css'
import type {
  CallQualityWizardIssueFormIssueType,
  CallQualityWizardIssueFormSchema,
  CallQualityWizardIssueFormSubmissionSchema,
} from './callQualityWizardIssueFormCommon'
import {
  callQualityWizardIssueFormIssue,
  callQualityWizardIssueFormSchema,
  callQualityWizardIssueFormIssues,
} from './callQualityWizardIssueFormCommon'

type CallQualityWizardIssueFormProps = {
  onSubmit?: (submission: CallQualityWizardIssueFormSubmissionSchema) => void
  onIssueChange?: (issue: CallQualityWizardIssueFormIssueType) => void
}

function CallQualityWizardIssueForm({
  onIssueChange,
  ...props
}: CallQualityWizardIssueFormProps) {
  const form = useForm<CallQualityWizardIssueFormSchema>({
    defaultValues: {
      issue: undefined,
      issueText: undefined,
    },
    resolver: zodResolver(callQualityWizardIssueFormSchema),
  })
  const onSubmit = (data: CallQualityWizardIssueFormSchema) => {
    props.onSubmit?.({
      type: 'issue',
      payload: {
        issue: data.issue,
        issueText: data.issue === 'other' ? data.issueText : '',
      },
    })
  }
  const selectedIssue = form.watch('issue')
  const hasSelectedIssue = typeof selectedIssue === 'string'
  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className={styles.form}>
      <div className={styles.formItems}>
        {callQualityWizardIssueFormIssues.map((key) => {
          const fieldProps = form.register('issue')
          const handleChange: ChangeEventHandler<HTMLInputElement> = (evt) => {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
            fieldProps.onChange(evt)
            onIssueChange?.(evt.target.value as CallQualityWizardIssueFormIssueType)
          }
          return (
            <div key={key} className={styles.radioButton}>
              <input
                type="radio"
                {...fieldProps}
                onChange={handleChange}
                value={key}
                id={`issue.${key}`}
                className={styles.radio}
              />
              <Typography
                component="label"
                htmlFor={`issue.${key}`}
                className={styles.radioLabel}
                variant="footnote"
              >
                {callQualityWizardIssueFormIssue[key]}
              </Typography>
            </div>
          )
        })}
      </div>
      {selectedIssue === 'other' ? (
        <div className={styles.textareaContainer}>
          <TextArea
            {...form.register('issueText')}
            fullWidth
            placeholder="Provide details of the issue..."
            rows={7}
            autoFocus
          />
        </div>
      ) : null}
      {/* eslint-disable-next-line @typescript-eslint/no-deprecated */}
      <Button disabled={!hasSelectedIssue} fullWidth>
        Submit feedback
      </Button>
    </form>
  )
}

export default CallQualityWizardIssueForm
